import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { fetchAnalysisData, AnalysisResponse, AnalysisParams } from '../../api/analysis';

// Define the state interface
export interface AnalysisState {
  analysisData: AnalysisResponse | null;
  loading: boolean;
  error: string | null;
  message: string | null;
  currentParams: AnalysisParams;
}

// Define initial state
const initialState: AnalysisState = {
  analysisData: null,
  loading: false,
  error: null,
  message: null,
  currentParams: { year1: 2024, year2: 2025 }
};

// Async thunk for fetching analysis data
export const fetchAnalysisAsync = createAsyncThunk(
  'analysis/fetchAnalysis',
  async () => {
    const response = await fetchAnalysisData();
    return response;
  }
);

// Create slice
const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearMessage: (state) => {
      state.message = null;
    },
    clearAnalysisData: (state) => {
      state.analysisData = null;
    },
    setAnalysisParams: (state, action: PayloadAction<AnalysisParams>) => {
      state.currentParams = { ...state.currentParams, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch analysis data
      .addCase(fetchAnalysisAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.message = null;
      })
      .addCase(fetchAnalysisAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.analysisData = action.payload;
        state.message = action.payload.message;
        state.error = null;
        // Update current params with the request params
        // if (action.payload.requestParams) {
        //   state.currentParams = { ...state.currentParams, ...action.payload.requestParams };
        // }
      })
      .addCase(fetchAnalysisAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch analysis data';
        state.analysisData = null;
      });
  },
});

// Export actions
export const {
  clearError,
  clearMessage,
  clearAnalysisData,
  setAnalysisParams,
} = analysisSlice.actions;

// Export reducer
export default analysisSlice.reducer;